/**
 * Shared error-message helpers.
 */

import { ApiError } from "./api/client";

// The #64 allergy/dietary guard rejects unsafe swaps/adds with a specific
// { error: "..." } message (surfaced by apiFetch as Error#message via
// ApiError). Prefer that message over a generic fallback so a user picking
// an allergen recipe sees exactly why, not a vague "Failed to update meal."
export function extractErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

// Analytics-safe failure detail. Failure events may carry a numeric HTTP
// status and NOTHING else from the error -- messages routinely embed user
// input (a pasted URL, an email, an invite code), and PostHog must stay free
// of PII. Non-ApiError throws (network/parse) report 0, matching apiFetch's
// own convention for those.
export function extractErrorStatus(error: unknown): number {
  return error instanceof ApiError ? error.status : 0;
}

// Machine-readable response code, straight off the parsed body's
// `details.code` field (the shape errorHandler.ts and MealPlanCommitError
// both use). Deliberately not an allow-list parser like
// parseCandidateMenuError in lib/api/meal-plans.ts -- that one rebuilds a
// privacy-safe DTO for UI rendering; this is for machine classification
// (e.g. retry policy) that just needs to know one specific code and must
// never guess from `error.message` text, which is meant for display.
export function extractErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (typeof data !== "object" || data === null) return undefined;
  const details = (data as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return undefined;
  const code = (details as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}
